import {
  useCatalogHistory,
  useCatalogItem,
  useCreateSpecimen,
  useMeta,
  useRemoveCatalogItem,
  useSession,
  useSpecimenList,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Card, CardContent, CardHeader, CardTitle } from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { ImageGallery } from '../../components/image-gallery.js'
import { PageLoading } from '../../components/page-loading.js'
import { MutationErrors } from '../../components/mutation-errors.js'
import { ChangeList } from '../../catalog/change-list.js'
import { LanguageToggle, withLanguage, type TextLanguage } from '../../catalog/language-toggle.js'
import { VisibilityBadge } from '../../catalog/shared.js'
import { useErrorText } from '../../lib/errors.js'
import { MetaView } from '../../meta/meta-view.js'
import { rootRoute } from '../root.js'

export const catalogItemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog/$id',
  component: CatalogItemPage,
})

function CatalogItemPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const { id } = catalogItemRoute.useParams()
  const item = useCatalogItem(id)
  const remove = useRemoveCatalogItem()
  const navigate = useNavigate()
  const [language, setLanguage] = useState<TextLanguage>('ru')

  if (item.error) return <Alert variant="destructive">{errorText.request(item.error)}</Alert>
  if (!item.data) return <PageLoading />
  const data = item.data
  const translated = Object.keys(data.originals).length > 0
  const values = withLanguage(data.values, data.originals, language)

  return (
    <article className="grid gap-6">
      <div className="flex flex-wrap items-start gap-3">
        <div className="mr-auto grid gap-1">
          <Link
            to="/catalog"
            search={{}}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← {t('catalog.back')}
          </Link>
          <h1 className="flex items-center gap-3 text-2xl font-semibold">
            {String(values.title ?? '')}
            <VisibilityBadge visibility={data.visibility} />
          </h1>
        </div>
        {data.canEdit ? (
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/catalog/$id/edit" params={{ id }} search={{}}>
                {t('catalog.edit')}
              </Link>
            </Button>
            <Button
              variant="ghost"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(t('catalog.deleteConfirm')))
                  remove.mutate(id, {
                    onSuccess: () => void navigate({ to: '/catalog', search: {} }),
                  })
              }}
            >
              {t('catalog.delete')}
            </Button>
          </div>
        ) : null}
      </div>
      <MutationErrors errors={[remove.error]} />
      <Mine id={id} />
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        {/* Фото остаётся на месте, пока читается длинное описание. */}
        <ImageGallery
          images={data.images}
          title={String(values.title ?? '')}
          className="lg:sticky lg:top-4"
        />
        <Card>
          <CardContent className="grid gap-4 pt-6">
            {translated ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">{t('translation.translatedNote')}</p>
                <LanguageToggle value={language} onChange={setLanguage} />
              </div>
            ) : null}
            <MetaView
              typeCode={data.typeCode}
              level="catalog"
              values={values}
              hide={['title']}
              grouped
            />
          </CardContent>
        </Card>
      </div>
      {data.variants.length ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('catalog.variants')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {data.variants.map((v) => (
              <section key={v.id} className="grid gap-2 border-t pt-4 first:border-t-0 first:pt-0">
                <h3 className="font-medium">{v.label}</h3>
                <MetaView typeCode={data.typeCode} level="variant" values={v.values} />
              </section>
            ))}
          </CardContent>
        </Card>
      ) : null}
      {data.ownerId === null ? <History id={id} /> : null}
    </article>
  )
}

/**
 * Связь записи каталога с личной коллекцией: что уже есть у пользователя, добавить экземпляр
 * или одним нажатием отметить «ищу».
 */
function Mine({ id }: { id: string }) {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const meta = useMeta().data
  const navigate = useNavigate()
  const mine = useSpecimenList({ catalogItemId: id, limit: 20 }, Boolean(session))
  const create = useCreateSpecimen()
  if (!session || !meta) return null
  const items = mine.data?.pages[0]?.items ?? []
  const wanted = meta.statuses.find((s) => s.code === 'wanted')
  const hasWanted = items.some((i) => i.values.status === wanted?.id)
  const statusName = (sid: unknown) => meta.statuses.find((s) => s.id === sid)?.name.ru
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card px-4 py-3 text-sm">
      {items.length ? (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">
            {t('catalog.own.have', { count: items.length })}
          </span>
          {items.map((i) => {
            const grade = i.values.grade as { value?: string } | undefined
            return (
              <Link
                key={i.id}
                to="/s/$id"
                params={{ id: i.id }}
                className="rounded-full border px-2.5 py-0.5 hover:border-primary"
              >
                {[grade?.value, statusName(i.values.status)].filter(Boolean).join(' · ') ||
                  t('catalog.own.specimen')}
              </Link>
            )
          })}
        </span>
      ) : (
        <span className="text-muted-foreground">{t('catalog.own.none')}</span>
      )}
      <div className="ml-auto flex flex-wrap gap-2">
        {wanted && !hasWanted ? (
          <Button
            variant="outline"
            size="sm"
            disabled={create.isPending}
            onClick={() =>
              create.mutate(
                { catalogItemId: id, values: { status: wanted.id } },
                { onSuccess: (s) => void navigate({ to: '/s/$id', params: { id: s.id } }) },
              )
            }
          >
            {t('catalog.own.want')}
          </Button>
        ) : null}
        <Button asChild size="sm">
          <Link to="/s/new" search={{ catalogItem: id }}>
            {t('catalog.own.add')}
          </Link>
        </Button>
      </div>
      <div className="w-full">
        <MutationErrors errors={[create.error]} />
      </div>
    </div>
  )
}

/** История правок общей записи — для редакторов; загружается при раскрытии. */
function History({ id }: { id: string }) {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const meta = useMeta().data
  const [open, setOpen] = useState(false)
  const editor = session?.user.role === 'admin' || session?.user.role === 'editor'
  const history = useCatalogHistory(id, editor && open)
  if (!editor) return null
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <button
            type="button"
            aria-expanded={open}
            className="flex items-center gap-2 text-left"
            onClick={() => setOpen((v) => !v)}
          >
            <span aria-hidden>{open ? '▾' : '▸'}</span>
            {t('history.title')}
          </button>
        </CardTitle>
      </CardHeader>
      {open && meta && history.data ? (
        <CardContent>
          <ChangeList meta={meta} changes={history.data} />
        </CardContent>
      ) : null}
    </Card>
  )
}
